import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Description as FileIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { API_URL } from '../../../utils/apiConfig';

// Must match I9_MAX_FILE_BYTES in server/controllers/specializedFormsController.js.
const I9_MAX_FILE_MB = 25;

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL

const I9FormUpload = ({ 
  formId, 
  employeeId, 
  onSubmit, 
  isCompleted, 
  documentStatus,
  employee,
}) => {
  const [files, setFiles] = useState({});
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploads, setUploads] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const documentTypes = {
    completed: { 
      label: 'Completed I-9 Form', 
      key: 'COMPLETED_I9',
      description: 'Signed I-9 form with employer verification'
    },
    listA: { 
      label: 'List A Document', 
      key: 'LIST_A',
      description: 'Passport or permanent resident card'
    },
    listB: { 
      label: 'List B Document', 
      key: 'LIST_B',
      description: 'Driver\'s license or state ID'
    },
    listC: { 
      label: 'List C Document', 
      key: 'LIST_C',
      description: 'Social Security card or birth certificate'
    }
  };

  useEffect(() => {
    const fetchUploads = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/forms/get-i9-uploads/${employeeId}/${formId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.uploads) {
            setUploads(data.uploads);
          }
        }
      } catch (err) {
        console.error('Error fetching uploads:', err);
        setError('Failed to fetch existing uploads');
      }
    };

    if (employeeId && formId) {
      fetchUploads();
    }
  }, [employeeId, formId]);

  const normalizeLabel = (label) => {
    const mapping = {
      "completed": "completed",
      "completedi9": "completed",
      "lista": "listA",
      "listb": "listB",
      "listc": "listC"
    };
    return mapping[label.toLowerCase()] || label.toLowerCase();
  };

  const handleFileChange = (type) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reject oversized files here rather than after a long upload. The server
    // enforces the same ceiling; without this the employee waits for the whole
    // file to transfer and then gets a failure with nothing to act on.
    if (file.size > I9_MAX_FILE_MB * 1024 * 1024) {
      const actualMb = (file.size / (1024 * 1024)).toFixed(1);
      setError(
        `"${file.name}" is ${actualMb} MB — the limit is ${I9_MAX_FILE_MB} MB per document. ` +
        `Photos taken on a phone are often over this. Try emailing the photo to yourself and ` +
        `choosing a smaller image size, or upload a screenshot of it instead.`
      );
      e.target.value = '';
      return;
    }

    setError(null);
    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const removeFile = (type) => {
    setFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[type];
      return newFiles;
    });
  };

  const replaceFile = async (type, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/forms/delete-i9-upload/${fileName}?employeeId=${employeeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (response.ok) {
        // Get updated uploads list
        const updatedResponse = await fetch(`${API_URL}/forms/get-i9-uploads/${employeeId}/${formId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
  
        if (updatedResponse.ok) {
          const updatedData = await updatedResponse.json();
          setUploads(updatedData.uploads);
  
          // Check remaining documents
          const hasListA = updatedData.uploads.some(upload => upload.label === 'LIST_A');
          const hasListB = updatedData.uploads.some(upload => upload.label === 'LIST_B');
          const hasListC = updatedData.uploads.some(upload => upload.label === 'LIST_C');
          const hasCompletedI9 = updatedData.uploads.some(upload => upload.label === 'COMPLETED_I9');
  
          // If we're missing required documents, mark as incomplete
          if (!hasCompletedI9 || !(hasListA || (hasListB && hasListC))) {
            console.log('Updating I-9 status to incomplete:', { employeeId, formId });
            const statusResponse = await fetch(`${API_URL}/employee_forms/update-status`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                employeeId,
                formId,
                isCompleted: false,
                documentStatus: 'Pending'
              })
            });
  
            if (!statusResponse.ok) {
              const errorData = await statusResponse.json();
              console.error('Update status error:', errorData);
              setError(`Failed to update form status: ${errorData.details || errorData.error || 'Unknown error'}`);
            } else if (onSubmit) {
              // Pass both formId and filePath for status update
              onSubmit(formId, null); // Update parent component
            }
          }
        }
  
        setFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[type];
          return newFiles;
        });
      }
    } catch (err) {
      console.error('Error replacing file:', err);
      setError('Failed to replace file');
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);
    
    // Set all files as uploading
    const uploadingStates = {};
    Object.keys(files).forEach(key => {
      uploadingStates[key] = true;
    });
    setUploadingFiles(uploadingStates);
  
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      const labels = {};
  
      formData.append('employeeId', employeeId.toString());
      formData.append('formId', formId.toString());
  
      // Map frontend types to backend expected field names
      const fieldNameMapping = {
        completed: 'completedI9_file',
        listA: 'listA_file',
        listB: 'listB_file',
        listC: 'listC_file'
      };
      
      Object.entries(files).forEach(([type, file]) => {
        const fieldname = fieldNameMapping[type];
        if (fieldname) {
          formData.append(fieldname, file);
        }
      });
  
      // Upload the files
      const response = await fetch(`${API_URL}/forms/upload-i9`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (!response.ok) {
        // The server already sends usable text here — server.js's multer handler
        // deliberately passes validation messages through ("File too large").
        // Discarding it for a bare "Upload failed" is what left emp273 with no
        // idea her photo was oversized.
        const detail = await response
          .json()
          .then(body => body.details || body.error)
          .catch(() => null);
        throw new Error(detail || `Upload failed (${response.status})`);
      }
  
      // Get all current uploads including the new ones
      const uploadsResponse = await fetch(`${API_URL}/forms/get-i9-uploads/${employeeId}/${formId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (uploadsResponse.ok) {
        const uploadsData = await uploadsResponse.json();
        setUploads(uploadsData.uploads);
  
        // Check ALL current documents (both existing and newly uploaded)
        const currentUploads = uploadsData.uploads;
        const hasListA = currentUploads.some(upload => normalizeLabel(upload.label) === 'listA');
        const hasListB = currentUploads.some(upload => normalizeLabel(upload.label) === 'listB');
        const hasListC = currentUploads.some(upload => normalizeLabel(upload.label) === 'listC');
        const hasCompletedI9 = currentUploads.some(upload => normalizeLabel(upload.label) === 'completed');
  
        // Always update the form status after upload
        // Complete if: Completed I-9 AND (List A OR (List B AND List C))
        const newStatus = hasCompletedI9 && (hasListA || (hasListB && hasListC));
        
        console.log('Updating I-9 status:', { employeeId, formId, newStatus, hasCompletedI9, hasListA, hasListB, hasListC });
        const statusResponse = await fetch(`${API_URL}/employee_forms/update-status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            employeeId,
            formId,
            isCompleted: newStatus,
            documentStatus: newStatus ? 'Completed' : 'Pending'
          })
        });
  
        if (!statusResponse.ok) {
          const errorData = await statusResponse.json();
          console.error('Update status error:', errorData);
          throw new Error(errorData.details || errorData.error || 'Failed to update form status');
        }
        
        // Update local state
        if (onSubmit) {
          // Pass both formId and filePath for status update
          onSubmit(formId, null);
        }
      }
  
      setFiles({});
      setUploadingFiles({});
  
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      setUploadingFiles({});
    } finally {
      setIsLoading(false);
    }
  };

  const getPreviewUrl = async (fileName) => {
    const token = localStorage.getItem('token');
    try {
      // Use direct API URL with token - iframes can load this directly
      const endpoint = fileName.includes('lista') ? 'lista' : 
                      fileName.includes('i9') ? 'i9' : 'document';
      return `${API_URL}/forms/i9-pdf/${employeeId}/${endpoint}?token=${token}`;
    } catch (error) {
      console.error('Error fetching I-9 document:', error);
      return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>I-9 Document Upload</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {isCompleted && <Alert severity="success" sx={{ mb: 2 }}>I-9 form and documents have been successfully submitted</Alert>}

          {Object.entries(documentTypes).map(([key, doc]) => (
            <Paper key={key} variant="outlined" sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant={isMobile ? "body1" : "subtitle1"} 
                sx={{ 
                  fontWeight: 600,
                  wordBreak: 'break-word',
                  lineHeight: 1.3
                }}
              >
                {doc.label}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 2,
                  fontSize: isMobile ? '0.875rem' : '0.875rem',
                  lineHeight: 1.4
                }}
              >
                {doc.description}
              </Typography>

              {/* Display existing uploads */}
              {uploads.filter(upload => normalizeLabel(upload.label) === key).map(upload => (
                <Box 
                  key={upload.fileName} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    p: 1
                  }}
                >
                  <FileIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      flexGrow: 1,
                      wordBreak: 'break-all',
                      fontSize: isMobile ? '0.813rem' : '0.875rem',
                      lineHeight: 1.4
                    }}
                  >
                    {upload.fileName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton 
                      onClick={async () => { 
                        setSelectedDocument(upload); 
                        setPreviewOpen(true);
                        setPreviewLoading(true);
                        const url = await getPreviewUrl(upload.fileName);
                        setPreviewUrl(url);
                        setPreviewLoading(false);
                      }} 
                      size="small"
                      sx={{ p: 0.5 }}
                    >
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton 
                      onClick={() => replaceFile(key, upload.fileName)}
                      size="small"
                      sx={{ p: 0.5 }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              {/* File upload section */}
              <Box sx={{ mt: 2 }}>
                {uploadingFiles[key] ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Uploading...
                    </Typography>
                  </Box>
                ) : files[key] ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileIcon />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {files[key].name}
                    </Typography>
                    <IconButton 
                      onClick={() => removeFile(key)}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    sx={{ 
                      width: '100%',
                      textTransform: 'none',
                      whiteSpace: 'normal',
                      height: 'auto',
                      py: 1.5,
                      px: 2
                    }}
                    disabled={isCompleted || uploads.some(upload => normalizeLabel(upload.label) === key)}
                  >
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Upload {doc.label}
                      </Typography>
                    </Box>
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange(key)}
                    />
                  </Button>
                )}
              </Box>
            </Paper>
          ))}

          {/* Submit button */}
          {Object.keys(files).length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isLoading || isCompleted}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? 'Uploading...' : 'Submit Documents'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Keep existing Dialog component for preview */}
      <Dialog 
        open={previewOpen} 
        onClose={() => {
          setPreviewOpen(false);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
          setSelectedDocument(null);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Document Preview</Typography>
            <IconButton onClick={() => {
              setPreviewOpen(false);
              if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
              }
              setSelectedDocument(null);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {previewLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={40} />
                <Typography color="textSecondary">Loading document...</Typography>
              </Box>
            ) : previewUrl ? (
              selectedDocument?.filePath.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={previewUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="Document Preview"
                />
              ) : (
                <img
                  src={previewUrl}
                  alt="Document Preview"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              )
            ) : (
              <Typography color="error">Failed to load document preview</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default I9FormUpload;